import {
  RuntimeSecretResolutionError,
  type RuntimeSecretsPlatformDef,
} from "@hoppscotch/common/platform/runtime-secrets"

async function request(
  teamID: string,
  environmentID: string,
  signal: AbortSignal,
  aliases?: string[]
) {
  const base = `${import.meta.env.VITE_BACKEND_API_URL}/teams/${encodeURIComponent(teamID)}/runtime-secrets`
  const controller = new AbortController()
  const abort = () => controller.abort()
  signal.addEventListener("abort", abort, { once: true })
  if (signal.aborted) abort()
  const timeout = setTimeout(abort, 20_000)
  try {
    const response = await fetch(
      aliases
        ? base
        : `${base}?environmentID=${encodeURIComponent(environmentID)}`,
      {
        method: aliases ? "POST" : "GET",
        credentials: "include",
        cache: "no-store",
        signal: controller.signal,
        headers: aliases ? { "Content-Type": "application/json" } : undefined,
        body: aliases ? JSON.stringify({ environmentID, aliases }) : undefined,
      }
    )
    if (!response.ok) {
      const messages: Record<number, string> = {
        401: "Sign in again to retrieve workspace credentials.",
        403: "You are no longer a member of this workspace.",
        400: "A required credential or environment is not configured for this workspace.",
        503: "Could not retrieve credentials from 1Password. Contact your workspace administrator or try again.",
      }
      throw new RuntimeSecretResolutionError(
        messages[response.status] ?? "Could not retrieve workspace credentials."
      )
    }
    return await response.json()
  } finally {
    clearTimeout(timeout)
    signal.removeEventListener("abort", abort)
  }
}

export const runtimeSecrets: RuntimeSecretsPlatformDef = {
  listAliases: async (teamID, environmentID, signal) =>
    (await request(teamID, environmentID, signal)).aliases,
  resolve: async (teamID, environmentID, aliases, signal) =>
    (await request(teamID, environmentID, signal, aliases)).values,
}
